import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal/Modal.jsx';
import ModalActions from '../Modal/ModalActions.jsx';
import Button from '../Button/Button.jsx';
import Text from '../Text/Text.jsx';
import FlexContainer from '../FlexContainer/FlexContainer.jsx';
import Dropdown from '../FormElements/Dropdown/Dropdown.jsx';
import NoResultsFoundMessage from '../NoResultsFoundMessage/NoResultsFoundMessage.jsx';
import ArrowSwitchVertical from '../Icons/ArrowSwitchVertical.jsx';
import ImageIcon from '../Icons/ImageIcon.jsx';
import useDragAndDrop from '../../hooks/useDragAndDrop.js';
import css from './SortableItemsModal.module.scss';

const MODAL_WIDTH = 'min(540px, calc(100vw - 32px))';

const SortableItemsModal = ({
  open,
  onClose,
  onSave,
  onReorder,
  title,
  items,
  saving = false,
}) => {
  const [orderedItems, setOrderedItems] = useState([]);

  useEffect(() => {
    if (open) {
      setOrderedItems(items.map((item, idx) => ({...item, position: idx + 1})));
    }
  }, [open, items]);

  const handleDragSortChange = () => {
    if (onReorder) {
      onReorder({method: 'drag'});
    }
  };

  const {
    draggedIndex,
    dragOverIndex,
    onDragStart,
    onDragEnter,
    onDragLeave,
    onDragEnd,
    onDragOver,
  } = useDragAndDrop(orderedItems, setOrderedItems, handleDragSortChange, 'position');

  const handleSaveClick = () => {
    onSave(orderedItems);
  };

  const handlePositionChange = (fromIdx, toPosition) => {
    const toIdx = toPosition - 1;
    if (toIdx === fromIdx || toIdx < 0 || toIdx >= orderedItems.length) {
      return;
    }

    setOrderedItems(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next.map((item, i) => ({...item, position: i + 1}));
    });

    if (onReorder) {
      onReorder({method: 'dropdown'});
    }
  };

  const footer = (
    <ModalActions align="right">
      <Button variant="tertiary" onClick={onClose} disabled={saving}>
        Cancel
      </Button>
      <Button onClick={handleSaveClick} disabled={saving}>
        Save
      </Button>
    </ModalActions>
  );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={title}
      stickyLayout
      bodyPadding={false}
      footer={footer}
      style={{width: MODAL_WIDTH}}
    >
      {orderedItems.length === 0 ?
        <NoResultsFoundMessage />
        :
        <ul className={css.sortableList}>
          {orderedItems.map((item, idx) =>
            <SortableRow
              key={item.id}
              item={item}
              idx={idx}
              totalCount={orderedItems.length}
              isDragged={draggedIndex === idx}
              isDragOver={dragOverIndex === idx}
              onDragStart={onDragStart}
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDragEnd={onDragEnd}
              onDragOver={onDragOver}
              onPositionChange={handlePositionChange}
            />
          )}
        </ul>
      }
    </Modal>
  );
};

const SortableRow = ({
  item,
  idx,
  totalCount,
  isDragged,
  isDragOver,
  onDragStart,
  onDragEnter,
  onDragLeave,
  onDragEnd,
  onDragOver,
  onPositionChange,
}) => {
  const handleMouseDown = (event) => {
    event.stopPropagation();
    onDragStart(idx);
  };

  const handleTouchStart = (event) => {
    event.stopPropagation();
    onDragStart(idx);
  };

  const handleDragEnter = () => {
    onDragEnter(idx);
  };

  const handleDragLeave = () => {
    onDragLeave(idx);
  };

  const handleDragOver = (event) => {
    onDragOver(event, idx);
  };

  const handlePositionSelectChange = (event) => {
    onPositionChange(idx, parseInt(event.target.value, 10));
  };

  const rowClassName = [
    css.sortableRow,
    isDragged ? css.dragged : '',
    isDragOver ? css.dragOver : '',
  ].filter(Boolean).join(' ');

  return (
    <li
      className={rowClassName}
      draggable
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragEnd={onDragEnd}
      onDragOver={handleDragOver}
    >
      <Text color="primary">
        <ArrowSwitchVertical />
      </Text>
      <PositionSelect
        value={idx + 1}
        count={totalCount}
        onChange={handlePositionSelectChange}
      />
      <SortableThumbnail thumbnailUrl={item.thumbnailUrl} name={item.name} />
      <FlexContainer
        flexDirection="column"
        flexFlow="column"
        gap={2}
        className={css.itemTextBlock}
      >
        <Text size="xs" color="muted" weight="semibold" className={css.itemCode}>
          {item.code}
        </Text>
        <Text size="small" weight="semibold" truncate className={css.itemName}>
          {item.name}
        </Text>
      </FlexContainer>
    </li>
  );
};

const PositionSelect = ({value, count, onChange}) => {
  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const options = Array.from({length: count}, (_, i) => ({
    value: i + 1,
    label: String(i + 1),
  }));

  return (
    <div
      className={css.positionSelector}
      draggable={false}
      onMouseDown={stopPropagation}
      onTouchStart={stopPropagation}
      onClick={stopPropagation}
    >
      <Dropdown
        size="small"
        value={value}
        onChange={onChange}
        options={options}
      />
    </div>
  );
};

const SortableThumbnail = ({thumbnailUrl, name}) => {
  if (thumbnailUrl) {
    return <img src={thumbnailUrl} alt={name || ''} className={css.thumbnail} />;
  }

  return (
    <div
      className={`${css.thumbnail} ${css.thumbnailPlaceholder}`}
      aria-label={name || ''}
    >
      <ImageIcon />
    </div>
  );
};

SortableItemsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onReorder: PropTypes.func,
  title: PropTypes.node.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    code: PropTypes.node,
    name: PropTypes.string,
    thumbnailUrl: PropTypes.string,
  })).isRequired,
  saving: PropTypes.bool,
};

export default SortableItemsModal;
